import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { IdleContext } from "../idle-context-file.part";
import {
  API_LOADING_ERROR,
  API_NOT_FOUND_ERROR,
  API_NO_DATA_FOUND_ERROR,
} from "@/lib/errors/error-constants";
import ScenariosErrorState from "@/components/States/scenariosErrorState";
import BarGraphErrorStates from "@/components/States/BarGraphErrorStates";
import styles from "@/page-components/idle-resource/idle.module.css";
import clsx from "clsx";
import CurrencyFormatter from "@/lib/value-converison";
import { Form } from "react-bootstrap";
import { usePotentialSavingAll } from "@/services/IdleResources/get-potential-saving-all.repo";

const ResourceBarGraph = ({ service }) => {
  const context = useContext(IdleContext);

  const query = useMemo(() => {
    const tags = (context?.selectedTags || []).filter(
      (e) => e.filterOn.length > 0 && e.values.length > 0
    );
    return {
      untagged: context.showUntaggedOnly,
      service: service,
      filters: {
        accounts: context?.selectAccounts?.length
          ? context.selectAccounts.map((e) => e.id)
          : undefined,
        rg: context?.selectedResourceGroup?.length
          ? context.selectedResourceGroup.map((e) => e.id)
          : undefined,
        regions: context?.selectRegions?.length
          ? context.selectRegions.map((e) => e.id)
          : undefined,
        tags: tags?.length ? tags : undefined,
      },
    };
  }, [
    context.selectAccounts,
    context.selectRegions,
    context.selectedResourceGroup,
    context?.selectedTags,
    context.showUntaggedOnly,
    service,
  ]);

  const { data, error, isLoading, refetch } = usePotentialSavingAll(query);

  useEffect(() => {
    context.setSemiCardRefetch(() => refetch);
  }, [refetch]);

  const transformChartData = (rawData) => {
    if (!rawData || typeof rawData !== "object") return [];

    const labelMap = {
      "0_7_days": "0-7 Days",
      "8_15_days": "8-15 Days",
      "16_30_days": "16-30 Days",
      "31_more_days": "More than 31 Days",
    };

    return Object.entries(rawData).map(([rangeKey, data]) => ({
      name: labelMap[rangeKey],
      ...data,
    }));
  };

  const chartData = transformChartData(data?.savings);

  const getBarConfigByService = (service) => {
    const normalized = service.toLowerCase();
    const isAws = normalized === "aws";
    const isAzure = normalized === "azure";
    const isGcp = normalized === "gcp";

    return [
      {
        label: isAzure ? "Public IPs" : isGcp ? "Static IPs" : "Elastic IPs",
        key: "ip",
        color: "#82B3AF",
      },
      {
        label: isAzure ? "VMs" : isGcp ? "Compute Engine" : "EC2",
        key: "vm",
        color: "#E08267",
      },
      {
        label: isAws ? "EBS" : "Disks",
        key: "disk",
        color: "#8FBBEA",
      },

      {
        label: "Load Balancers",
        key: "load_balancer",
        color: "#EBB78A",
      },
    ];
  };
  const barConfig = getBarConfigByService(service);

  return (
    <div style={{ width: "100%", marginBottom: "38px", marginLeft: "12px" }}>
      {isLoading && <BarGraphErrorStates />}
      {!isLoading && (error || data) && (
        <div className={clsx("contaier", styles.IdleResource_heading)}>
          Idle Resources
        </div>
      )}
      {error && (
        <div style={{ paddingRight: "32px" }}>
          <ScenariosErrorState
            error={error.message}
            messageConfig={{
              [API_LOADING_ERROR]: {
                message: "Oops!",
                additionalMessage:
                  "We encountered a problem loading your data. Please ensure a stable internet connection and try again. If the issue persists, contact support.",
              },
              [API_NOT_FOUND_ERROR]: {
                message: "404 Error",
                additionalMessage:
                  "We couldn’t find the data you’re looking for. This might be due to a temporary issue or incorrect resource. Please check back later or contact support if the problem persists.",
              },
              [API_NO_DATA_FOUND_ERROR]: {
                message: "No Idle Resource Data Available",
                additionalMessage:
                  "We couldn’t retrieve idle resource data at this moment. This might be due to an absence of data or a temporary issue. Please try again later.",
              },
            }}
          />
        </div>
      )}

      {data && (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            data={chartData}
            margin={{ top: 30, right: 30, left: 0, bottom: 0 }}
          >
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={false}
              fontSize={10}
              fontWeight={500}
              stroke="var(--Neutral-D3, #495057)"
            />
            <YAxis
              label={{
                value: "No. of Idle Resources",
                angle: -90,
                fontSize: 10,
                fontWeight: 400,
                dx: -8,
              }}
              tickLine={false}
              axisLine={false}
              fontSize={10}
              fontWeight={400}
              stroke="var(--Neutral-D3, #495057)"
              tickFormatter={(tick) => (Number.isInteger(tick) ? tick : "")}
            />
            <CartesianGrid strokeDasharray="0" stroke="#F0F0F0" />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "transparent" }}
            />
            <Legend
              align="center"
              verticalAlign="bottom"
              layout="horizontal"
              iconSize={12}
              iconType="Square"
              fontSize={12}
              fontWeight={400}
              fontStyle="normal"
            />

            {barConfig.map(({ key, label, color }) => (
              <Bar
                key={key}
                dataKey={key}
                name={label}
                fill={color}
                barSize={50}
              />
            ))}

            {/* {barKeys.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              fill={colors[i]}
              barSize={50}
              onClick={(data) => {
                setSelectedBar({
                  resourceType: key,
                  idleDay: data.name,
                  value: data[key],
                });
              }}
              shape={(props) => (
                <CustomBarShape
                  {...props}
                  dataKey={key}
                  selectedBarKey={selectedBar?.resourceType}
                  selectedIdleDay={selectedBar?.idleDay}
                />
              )}
            />
          ))} */}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default ResourceBarGraph;

const colorsMap = {
  "Elastic IPs": "#72B5A4",
  EBS: "#7FB3FF",
  EC2: "#ED8663",
  "Load Balancers": "#F2B77F",
  "Public IPs": "#72B5A4",
  Disks: "#7FB3FF",
  VMs: "#ED8663",
  "Static IPs": "#72B5A4",
  "Compute Engine": "#ED8663",
};

const CustomBarShape = ({
  x,
  y,
  width,
  height,
  fill,
  payload,
  selectedBarKey,
  selectedIdleDay,
  dataKey,
}) => {
  const isSelected =
    selectedBarKey === dataKey && payload.name === selectedIdleDay;

  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      stroke={isSelected ? "red" : "none"}
      strokeWidth={isSelected ? 2 : 0}
      rx={2}
    />
  );
};

export const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const potentialSaving = payload[0]?.payload?.potential_saving || 0;
  if (active) {
    return (
      <div className={styles.custom_tooltip}>
        <div className={styles.tooltip_date} style={{ paddingLeft: "8px" }}>
          <div className={styles.tooltip_date_css}>{label}</div>
        </div>
        <div
          style={{ paddingRight: "12px", width: "100%", paddingLeft: "8px" }}
        >
          <table>
            <tbody>
              {payload.map((entry, index) => (
                <tr
                  key={`${styles.tooltip_row} ${index}`}
                  className={styles.horizontalline_tool}
                >
                  <td
                    className={styles.cloud_cell}
                    style={{ padding: "4px 24px 4px 8px", fontSize: 11 }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: "10%",
                        // backgroundColor: colorsMap[entry.name] || "#ccc",
                        backgroundColor: entry?.fill,
                        display: "inline-block",
                        marginRight: 8,
                      }}
                    />
                    {entry.name}
                  </td>
                  <td
                    className={styles.cost_cell}
                    style={{ padding: "4px 24px 4px 8px" }}
                  >
                    {entry?.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontWeight: "500",
              fontSize: 12,
              color: "#CED4DA",
              padding: "4px 4px 12px 8px",
            }}
          >
            <span>Potential Savings</span>

            <span>
              <CurrencyFormatter value={potentialSaving || 0} />
            </span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
